// Orquesta todas las pantallas HTML: hub, HUD, modales, tutorial y resultados.
// Delega las acciones de juego en `handlers` (proporcionados por App).
import { ModalManager } from './ModalManager.js';
import { HubScreen } from './HubScreen.js';
import { GameHUD } from './GameHUD.js';
import { HandPanel } from './HandPanel.js';
import { ResultsScreen } from './ResultsScreen.js';
import { TutorialManager } from './TutorialManager.js';
import { FriendsUI } from './FriendsUI.js';
import { ProfileUI } from './ProfileUI.js';
import { pwa } from '../core/PWA.js';
import { ICON } from './icons.js';
import { GAME_MODE, AI_LEVEL } from '../utils/constants.js';
import { GAME_NAME } from '../core/Config.js';
// Iconos (imagen) de cada modo de juego para el menú de JUGAR.
import imgClassic from '../assets/icon-classic.webp';
import imgQuick from '../assets/icon-quick.webp';
import imgTimed from '../assets/icon-timed.webp';
import imgDaily from '../assets/icon-daily.webp';
import imgSurvival from '../assets/icon-survival.webp';
import imgOnline from '../assets/icon-online.webp';
// Planchas (imagen) de cada nivel de dificultad.
import imgDiffEasy from '../assets/diff-easy.webp';
import imgDiffNormal from '../assets/diff-normal.webp';
import imgDiffHard from '../assets/diff-hard.webp';
import imgRivalAvatar from '../assets/avatar-ia-1.webp';

// Avatar para una fila del ranking: imagen (IA / foto) o emoji (humano).
function rankAvatar(av) {
  const s = String(av || '');
  if (s.startsWith('data:') || s.startsWith('http') || s.startsWith('/') || /\.(webp|png|jpe?g|svg)/i.test(s) || s.includes('assets')) {
    // Si no carga (URL sin internet), se sustituye por el emoji por defecto (no cuadro roto).
    return `<img class="ava-img" src="${s}" alt="" onerror="this.outerHTML='<span>🙂</span>'" />`;
  }
  if (s && s !== 'default' && s !== 'custom') return `<span>${s}</span>`;
  return '<span>🙂</span>';
}


export class UIManager {
  constructor({ root, bus, game, progression, audio, config, handlers, friendNet, ranking }) {
    this.root = root;
    this.bus = bus;
    this.game = game;
    this.progression = progression;
    this.audio = audio;
    this.config = config;
    this.handlers = handlers;
    this.friendNet = friendNet;
    this.ranking = ranking; // servicio de ranking global (Firestore)

    this.modals = new ModalManager(root, bus);
    this.results = new ResultsScreen(this.modals);

    this.hub = new HubScreen(root, {
      progression,
      bus,
      onPlay: () => this.openModeSelect(),
      onTutorial: () => handlers.onTutorial(),
      onOpen: (id) => this.openMenu(id),
      onInstall: () => this.installApp(),
    });

    this.hud = new GameHUD(root, {
      bus,
      game,
      actions: {
        confirm: () => handlers.onConfirm(),
        undo: () => handlers.onUndo(),
        reset: () => handlers.onReset(),
        pass: () => handlers.onPass(),
        pause: () => this.openPause(),
        showWords: () => this.openWords(),
        help: () => this.openHudHelp(),
      },
    });

    // Mano del jugador como cartas HTML (separada del tablero 3D).
    this.handPanel = new HandPanel(root, {
      bus,
      game,
      onDropToBoard: (id, x, y) => handlers.onDropFromHand(id, x, y),
      isInteractive: () => !!game.state && !game.state.finished && game.isSelfTurn(),
      toast: (msg, opts) => this.toast(msg, opts),
    });

    // Multijugador con amigos (P2P). La sincronización de la partida es la fase siguiente.
    this.friends = new FriendsUI({
      modals: this.modals,
      bus,
      progression,
      friendNet,
      onStart: (net, opts) => handlers.onStartOnline(net, opts),
    });
  }

  openFriends(prefillCode = null) {
    this.friends.open(prefillCode);
  }

  /** Instalar la app (PWA): prompt nativo, o instrucciones según el navegador. */
  installApp() {
    if (pwa.canPrompt()) { pwa.prompt(); return; }
    const secure = window.isSecureContext;
    let body;
    if (pwa.isIOS()) {
      body = 'En iPhone/iPad (Safari): pulsa <b>Compartir</b> (el cuadrado con la flecha ↑) y luego <b>«Añadir a pantalla de inicio»</b>.';
    } else if (!secure) {
      body = '⚠️ Para instalar, la web debe abrirse por <b>https://</b> (no por http:// ni por IP). Abre el dominio con HTTPS e inténtalo de nuevo.';
    } else {
      body = 'Abre el <b>menú del navegador</b> (⋮ en Chrome de Android) y pulsa <b>«Instalar aplicación»</b> o <b>«Añadir a pantalla de inicio»</b>.<br><br>Si no aparece: recarga la página, interactúa unos segundos y vuelve a abrir el menú.';
    }
    this.modals.open({
      title: '⬇️ Instalar PALABRIKS',
      dismissible: true,
      bodyHTML: `<p class="modal-text">${body}</p>`,
      buttons: [{ label: 'Entendido', className: 'btn-primary' }],
    });
  }

  // Perfil del jugador (nombre + avatar).
  _initProfile() {
    if (this.profile) return;
    this.profile = new ProfileUI({
      modals: this.modals,
      progression: this.progression,
      bus: this.bus,
      onSaved: () => {
        this.hub.refresh();
        this.hud.refresh?.();
        // Actualiza los datos que se envían en las invitaciones de amigos.
        if (this.friendNet) {
          const p = this.progression.profile;
          const av = p.avatar && p.avatar !== 'default' && p.avatar !== 'custom' ? p.avatar : '🙂';
          this.friendNet.me = { name: p.name, avatar: av, code: p.friendCode };
        }
      },
    });
  }
  openProfile(opts = {}) { this._initProfile(); this.profile.open(opts); }
  isOnboarded() { this._initProfile(); return this.profile.isOnboarded; }

  /** Splash a pantalla completa de VICTORIA / DERROTA. Resuelve tras ~3 s. */
  showEndSplash(isWin) {
    return new Promise((resolve) => {
      const el = document.createElement('div');
      el.className = `end-splash ${isWin ? 'win' : 'lose'}`;
      const particles = isWin
        ? `<div class="es-sparks">${Array.from({ length: 22 }).map((_, i) => `<span style="--i:${i}"></span>`).join('')}</div>`
        : `<div class="es-embers">${Array.from({ length: 16 }).map((_, i) => `<span style="--i:${i}"></span>`).join('')}</div>`;
      el.innerHTML = `
        ${isWin ? '<div class="es-rays"></div><div class="es-flash"></div>' : '<div class="es-veil"></div>'}
        ${particles}
        <div class="es-inner">
          <div class="es-medal">
            <span class="es-medal-emoji">${isWin ? '🏆' : '💔'}</span>
          </div>
          <div class="es-title" data-text="${isWin ? '¡VICTORIA!' : 'DERROTA'}">${isWin ? '¡VICTORIA!' : 'DERROTA'}</div>
          <div class="es-sub">${isWin ? '¡Enhorabuena, has ganado la partida!' : 'Esta vez no ha podido ser…'}</div>
        </div>`;
      this.root.appendChild(el);
      requestAnimationFrame(() => el.classList.add('show'));
      setTimeout(() => {
        el.classList.add('out');
        setTimeout(() => { el.remove(); resolve(); }, 450);
      }, 3600);
    });
  }

  // --- Cambio de pantallas ---
  showHub() {
    this.hud.hide();
    this.handPanel.hide();
    this.hub.show();
  }
  showGame() {
    this.hub.hide();
    this.hud.show();
    this.handPanel.show();
  }

  toast(msg, opts) {
    this.modals.toast(msg, opts);
  }

  // --- Selección de modo ---
  openModeSelect() {
    // Solo Clásico y Reta a un amigo están desbloqueados de inicio. Cada uno de los demás modos
    // se desbloquea al conseguir un LOGRO CONCRETO y temático (`unlockAch` = id del logro).
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const modes = [
      { id: GAME_MODE.CLASSIC, color: '#46c25a', label: 'Clásico', desc: 'El primero en quedarse sin fichas gana.' },
      { id: GAME_MODE.ONLINE, color: '#3aa0a0', label: 'Reta a un amigo', desc: 'Partida 1 contra 1: crea una sala e invítale.' },
      { id: GAME_MODE.ASYNC, color: '#7ed957', label: 'Reta a un jugador', desc: 'Partidas 1v1 por turnos: juega a tu ritmo, cada uno cuando pueda.' },
      { id: GAME_MODE.QUICK, color: '#f2c14e', label: 'Partida rápida', desc: 'Al toque: tú contra 1 rival (Normal).', unlockAch: 'games-10' },
      { id: GAME_MODE.TIMED, color: '#3a80d0', label: 'Contrarreloj', desc: 'Como Clásico, pero 25 s por turno.', unlockAch: 'combo-5' },
      { id: GAME_MODE.DAILY, color: '#e8622a', label: 'Puzzle diario', desc: 'Mismas fichas para todos hoy.', unlockAch: 'wordsmith' },
      { id: GAME_MODE.SURVIVAL, color: '#8a4fc4', label: 'Supervivencia', desc: 'Cada ronda sube la dificultad.', unlockAch: 'wins-20' },
    ];
    const body = `<div class="modes-scroll">${modes
      .map((m, i) => {
        const locked = m.unlockAch && !this.progression.isAchievementUnlocked(m.unlockAch);
        const ach = m.unlockAch ? this.progression.getAchievement(m.unlockAch) : null;
        let lockHTML = '';
        if (locked && ach) {
          const { progress, target } = this.progression.achievementProgress(m.unlockAch);
          lockHTML = `<span class="mode-lock">🔒 Logro «${esc(ach.label)}» · ${progress}/${target}</span>`;
        }
        // Badge "te toca jugar" en "Reta a un jugador".
        const turns = (m.id === GAME_MODE.ASYNC) ? (this.handlers.asyncTurnCount?.() || 0) : 0;
        const badge = turns > 0 ? `<span class="mode-turn-badge" title="Te toca jugar">${turns}</span>` : '';
        return `<button class="diff-card mode-opt${locked ? ' locked' : ''}"
          data-mode="${m.id}"
          ${locked ? `data-need-label="${esc(ach?.label || '')}" data-need-desc="${esc((ach?.desc || '').replace(/\.$/, ''))}"` : ''}
          style="--mc:${m.color};--d:${i * 0.05}s">
        <span class="dc-label">${m.label}</span>
        <span class="dc-desc">${m.desc}</span>
        ${lockHTML}${badge}
      </button>`;
      })
      .join('')}</div>`;

    const modal = this.modals.open({ title: 'MODOS DE JUEGO', className: 'mode-modal modes-modal modes-scroll-modal', bodyHTML: body, dismissible: true });
    // Botón de cerrar (círculo con X) arriba a la derecha, con separación.
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);
    modal.querySelectorAll('[data-mode]').forEach((b) => {
      b.addEventListener('click', () => {
        const needLabel = b.getAttribute('data-need-label');
        if (needLabel) { // modo bloqueado: indica QUÉ logro concreto lo desbloquea
          const desc = b.getAttribute('data-need-desc');
          this.toast(`Consigue el logro «${needLabel}» (${desc}) para desbloquear este modo.`, { icon: '🔒', duration: 3200 });
          this.bus.emit('audio:sfx', { name: 'invalid' });
          return;
        }
        this.modals.close();
        this._routeMode(b.getAttribute('data-mode'));
      });
    });
  }

  /** Encamina cada modo a su configuración. */
  _routeMode(mode) {
    if (mode === GAME_MODE.ONLINE) {
      this.openFriends();
    } else if (mode === GAME_MODE.ASYNC) {
      this.openAsyncMatches();
    } else if (mode === GAME_MODE.QUICK) {
      // Partida rápida: sin configurar, contra 1 IA en Normal. Empieza directamente.
      this.handlers.onStartGame({ mode, aiLevel: AI_LEVEL.NORMAL, numAI: 1 });
    } else if (mode === GAME_MODE.DAILY || mode === GAME_MODE.SURVIVAL) {
      this.openSoloIntro(mode);
    } else {
      // Clásico y Contrarreloj usan la misma pantalla de dificultad (nivel + rivales).
      this.openDifficulty(mode);
    }
  }

  /** Intro breve para modos en solitario (Puzzle diario / Supervivencia). */
  openSoloIntro(mode) {
    const info = mode === GAME_MODE.DAILY
      ? { title: 'PUZZLE DIARIO', desc: 'Mismas fichas para todos hoy. Consigue la máxima puntuación y construye la palabra del día. Pulsa «Pasar» para terminar.' }
      : { title: 'SUPERVIVENCIA', desc: 'Cada ronda debes crear una palabra más larga y con menos tiempo. ¿Cuántas rondas aguantas?' };
    this.modals.open({
      title: info.title,
      bodyHTML: `<p class="modal-text">${info.desc}</p>`,
      dismissible: true,
      buttons: [
        { label: 'Atrás', className: 'btn-ghost' },
        { label: 'Comenzar', className: 'btn-primary', onClick: () => this.handlers.onStartGame({ mode, numAI: 0 }) },
      ],
    });
  }

  /** Configuración de Contrarreloj: dificultad, rivales y segundos por turno. */
  openTimedSetup(mode) {
    const levels = [
      { id: AI_LEVEL.EASY, label: 'Fácil' },
      { id: AI_LEVEL.NORMAL, label: 'Normal' },
      { id: AI_LEVEL.HARD, label: 'Difícil' },
    ];
    const body = `
      <div class="field-row"><label>Dificultad IA</label>
        <select id="timed-level">${levels.map((l) => `<option value="${l.id}">${l.label}</option>`).join('')}</select></div>
      <div class="field-row"><label>Rivales IA</label>
        <select id="timed-ai"><option value="1">1</option><option value="2">2</option><option value="3">3</option></select></div>
      <div class="field-row"><label>Segundos por turno</label>
        <select id="timed-secs">${(this.config.timer.options || [30, 45, 60]).map((s) => `<option value="${s}" ${s === (this.config.timer.default || 60) ? 'selected' : ''}>${s}s</option>`).join('')}</select></div>`;
    const modal = this.modals.open({
      title: '⏱️ Contrareloj',
      bodyHTML: body,
      dismissible: true,
      buttons: [
        { label: 'Atrás', className: 'btn-ghost' },
        {
          label: 'Comenzar',
          className: 'btn-primary',
          onClick: () => this.handlers.onStartGame({
            mode,
            aiLevel: modal.querySelector('#timed-level').value,
            numAI: parseInt(modal.querySelector('#timed-ai').value, 10),
            timerSeconds: parseInt(modal.querySelector('#timed-secs').value, 10),
          }),
        },
      ],
    });
  }

  openDifficulty(mode) {
    const levels = [
      { id: AI_LEVEL.EASY, img: imgDiffEasy, color: '#7ed957', label: 'Fácil', desc: 'Palabras cortas, poca reorganización.' },
      { id: AI_LEVEL.NORMAL, img: imgDiffNormal, color: '#f2c14e', label: 'Normal', desc: 'Evoluciona palabras y usa afijos.' },
      { id: AI_LEVEL.HARD, img: imgDiffHard, color: '#e5533a', label: 'Dificil', desc: 'Busca cadenas y vaciar su atril.' },
    ];
    let selLevel = AI_LEVEL.NORMAL;
    let selAI = 3;
    const body = `
      <p class="sub-hint">Elige el nivel y el número de rivales.</p>
      <div class="diff-grid">${levels
        .map((l, i) => `<button class="diff-card${l.id === selLevel ? ' selected' : ''}" data-level="${l.id}"
            style="--mc:${l.color};--d:${i * 0.05}s" aria-pressed="${l.id === selLevel}">
          <span class="dc-label">${l.label.toUpperCase()}</span>
          <span class="dc-desc">${l.desc}</span>
          <span class="dc-check" aria-hidden="true">✓</span>
        </button>`)
        .join('')}</div>
      <div class="opt-row">
        <span class="opt-label"><span class="opt-ic"><img src="${imgRivalAvatar}" alt=""></span> Rivales</span>
        <div class="seg" role="group" aria-label="Número de rivales">
          ${[1, 2, 3].map((n) => `<button class="seg-btn${n === selAI ? ' on' : ''}" data-ai="${n}" aria-pressed="${n === selAI}">${n}</button>`).join('')}
        </div>
      </div>`;
    const modal = this.modals.open({
      title: 'DIFICULTAD',
      className: 'mode-modal modes-modal',
      dismissible: true,
      bodyHTML: body,
    });
    // Atrás: círculo con X arriba a la derecha (como en el resto de ventanas).
    const backBtn = document.createElement('button');
    backBtn.className = 'modal-close';
    backBtn.setAttribute('aria-label', 'Volver');
    backBtn.innerHTML = '✕';
    backBtn.addEventListener('click', () => { this.modals.close(); this.openModeSelect(); });
    modal.appendChild(backBtn);
    // Comenzar: círculo con ▶, abajo a la derecha, con rebote.
    const startBtn = document.createElement('button');
    startBtn.className = 'lobby-start';
    startBtn.setAttribute('aria-label', 'Comenzar');
    startBtn.innerHTML = '▶';
    startBtn.addEventListener('click', () => { this.modals.close(); this.handlers.onStartGame({ mode, aiLevel: selLevel, numAI: selAI }); });
    modal.appendChild(startBtn);
    modal.querySelectorAll('[data-level]').forEach((b) => {
      b.addEventListener('click', () => {
        selLevel = b.getAttribute('data-level');
        modal.querySelectorAll('.diff-card').forEach((c) => {
          const on = c === b;
          c.classList.toggle('selected', on);
          c.setAttribute('aria-pressed', on);
        });
        this.bus.emit('audio:sfx', { name: 'pick' });
      });
    });
    modal.querySelectorAll('[data-ai]').forEach((b) => {
      b.addEventListener('click', () => {
        selAI = parseInt(b.getAttribute('data-ai'), 10);
        modal.querySelectorAll('.seg-btn').forEach((c) => {
          const on = c === b;
          c.classList.toggle('on', on);
          c.setAttribute('aria-pressed', on);
        });
        this.bus.emit('audio:sfx', { name: 'pick' });
      });
    });
  }

  // --- Palabras en la mesa (ventana emergente) ---
  openWords() {
    const words = this.game.board?.words || [];
    let bad = 0;
    const rows = words.map((w) => {
      const res = this.game.validator.validateWord(w.tiles);
      if (!res.valid) bad++;
      const text = this.game.board.wordText(w).toUpperCase() || '—';
      return `<div class="word-row ${res.valid ? 'ok' : 'bad'}">
        <span class="wr-mark">${res.valid ? '✓' : '✕'}</span>
        <span class="wr-text">${text}</span></div>`;
    }).join('');
    const body = words.length
      ? `<div class="words-modal-list">${rows}</div>
         <p class="sub-hint">${bad ? `Hay ${bad} palabra(s) sin validar (✕).` : '¡Todas las palabras son válidas! ✓'}</p>`
      : '<p class="modal-text">Aún no hay palabras en la mesa. Arrastra fichas para construir palabras.</p>';
    const modal = this.modals.open({
      title: 'PALABRAS EN LA MESA',
      className: 'words-modal',
      bodyHTML: body,
      dismissible: true,
    });
    // Cerrar con círculo + X arriba a la derecha (como en el resto de ventanas).
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);
  }

  // --- Ayuda del HUD (¿para qué sirve cada botón?) ---
  openHudHelp() {
    // Mismos iconos (SVG neón) que los botones del juego, cada uno en su color de acento.
    const SVG = {
      reset: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v6h6"/><path d="M3.5 9a9 9 0 1 1-1.2 6"/></svg>',
      undo: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M9 7 4 12l5 5"/><path d="M4 12h11a5 5 0 0 1 0 10"/></svg>',
      pass: '<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linejoin="round"><path d="M5 5l8 7-8 7z"/><rect x="16" y="5" width="3" height="14" rx="1.2"/></svg>',
      confirm: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
      words: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M8 7h12M8 12h12M8 17h12"/><circle cx="4" cy="7" r="1.3" fill="currentColor" stroke="none"/><circle cx="4" cy="12" r="1.3" fill="currentColor" stroke="none"/><circle cx="4" cy="17" r="1.3" fill="currentColor" stroke="none"/></svg>',
      pause: '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4.4" height="14" rx="1.4"/><rect x="13.6" y="5" width="4.4" height="14" rx="1.4"/></svg>',
      bag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9.5a6 6 0 0 1 12 0V19a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 19z"/><path d="M9 9.5V7a3 3 0 0 1 6 0v2.5"/></svg>',
      clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7.5V12l3.2 1.9"/></svg>',
      move: '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>',
      tap: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3.2" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="7.6" opacity="0.65"/></svg>',
    };
    const items = [
      { ic: SVG.reset, acc: '#f0785a', label: 'Reiniciar', desc: 'borra la jugada del turno.' },
      { ic: SVG.undo, acc: '#5fe0d0', label: 'Deshacer', desc: 'deshace tu último movimiento.' },
      { ic: SVG.pass, acc: '#b98cf5', label: 'Pasar', desc: 'robas 1 ficha y cedes el turno.' },
      { ic: SVG.confirm, acc: '#a3e15c', label: 'Confirmar', desc: 'puntúa tu jugada y cede el turno.' },
      { ic: SVG.words, acc: '#5fe0d0', label: 'Palabras', desc: 'lista de palabras en la mesa.' },
      { ic: SVG.pause, acc: '#5fe0d0', label: 'Pausa', desc: 'reanudar, ajustes o abandonar.' },
      { ic: SVG.bag, acc: '#f2cb4d', label: 'Bolsa', desc: 'fichas que quedan por salir.' },
      { ic: SVG.clock, acc: '#4bb6e8', label: 'Reloj', desc: 'tiempo de cada turno.' },
      { ic: SVG.move, acc: '#b98cf5', label: 'Mover la mano', desc: 'arrastra el tirador para reubicar fichas.' },
      { ic: SVG.tap, acc: '#a3e15c', label: 'Doble toque', desc: 'reorganiza y ajusta el zoom de la mesa.' },
    ];
    const body = `
      <ul class="help-list">
        ${items.map((it) => `<li><span class="help-ic" style="--acc:${it.acc}">${it.ic}</span><span class="help-tx"><b>${it.label}</b> — ${it.desc}</span></li>`).join('')}
      </ul>`;
    const modal = this.modals.open({
      title: 'AYUDA',
      className: 'help-modal',
      bodyHTML: body,
      dismissible: true,
    });
    // Cerrar con círculo + X arriba a la derecha (como en el resto de ventanas).
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);
  }

  // --- Pausa (mismo estilo que la selección de dificultad: tarjetas en fila) ---
  openPause() {
    const opts = [
      { p: 'resume', color: '#59e06a', label: 'Reanudar', desc: 'Volver a la partida.' },
      { p: 'settings', color: '#f2c14e', label: 'Ajustes', desc: 'Sonido, vibración y opciones.' },
      { p: 'resign', color: '#e5533a', label: 'Salir', desc: 'Abandonar la partida.' },
    ];
    const body = `
      <div class="diff-grid">${opts
        .map((o, i) => `<button class="diff-card" data-p="${o.p}" style="--mc:${o.color};--d:${i * 0.05}s">
          <span class="dc-label">${o.label.toUpperCase()}</span>
          <span class="dc-desc">${o.desc}</span>
        </button>`)
        .join('')}</div>`;
    const modal = this.modals.open({
      title: 'PAUSA',
      className: 'mode-modal modes-modal',
      dismissible: true,
      bodyHTML: body,
    });
    // Cerrar (= reanudar) con círculo + X arriba a la derecha.
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Reanudar');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);
    modal.querySelector('[data-p="resume"]').addEventListener('click', () => this.modals.close());
    modal.querySelector('[data-p="settings"]').addEventListener('click', () => this.openSettings());
    modal.querySelector('[data-p="resign"]').addEventListener('click', () => { this.modals.close(); this.handlers.onResign(); });
  }

  // --- Asignación de comodín universal ---
  openWildcardAssign(tile, onAssign) {
    const options = ['A', 'E', 'I', 'O', 'U', 'S', 'R', 'N', 'L', 'T', 'CA', 'SA', 'RE', 'CO'];
    const body = `<p class="modal-text">Elige qué representa el comodín:</p>
      <div class="wild-grid">${options.map((o) => `<button class="wild-opt" data-val="${o}">${o}</button>`).join('')}</div>`;
    const modal = this.modals.open({ title: '★ Comodín universal', bodyHTML: body, dismissible: true });
    modal.querySelectorAll('[data-val]').forEach((b) => {
      b.addEventListener('click', () => {
        onAssign(b.getAttribute('data-val'));
        this.modals.close();
      });
    });
  }

  // --- Selector de transformación (comodín ⇄) ---
  openTransformChooser(options, { onChoose, onCancel }) {
    const body = `<p class="modal-text">Elige la ficha equivalente:</p>
      <div class="wild-grid">${options.map((o) => `<button class="wild-opt" data-val="${o}">${o}</button>`).join('')}</div>`;
    const modal = this.modals.open({
      title: '⇄ Transformar ficha',
      bodyHTML: body,
      dismissible: true,
      buttons: [{ label: 'Cancelar', className: 'btn-ghost', onClick: () => onCancel?.() }],
    });
    modal.querySelectorAll('[data-val]').forEach((b) => {
      b.addEventListener('click', () => {
        onChoose(b.getAttribute('data-val'));
        this.modals.close();
      });
    });
  }

  // --- Ajustes ---
  openSettings() {
    const a = this.config.audio;
    const acc = this.config.accessibility;
    const accountEmail = this.progression.profile.google?.email || '';
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const body = `
      <div class="set-grid">
        <div class="set-col">
          <div class="settings-group" style="flex-grow:5">
            <h3>Audio</h3>
            ${slider('master', 'General', a.master)}
            ${slider('music', 'Música', a.music)}
            ${slider('sfx', 'Efectos', a.sfx)}
            ${toggle('muted', 'Silenciar', a.muted)}
            ${toggle('vibration', 'Vibración', a.vibration)}
          </div>
        </div>
        <div class="set-col">
          <div class="settings-group" style="flex-grow:3">
            <h3>Accesibilidad</h3>
            ${slider('textScale', 'Tamaño de texto', acc.textScale, 0.8, 1.6)}
            ${toggle('highContrast', 'Alto contraste', acc.highContrast)}
            ${toggle('reduceMotion', 'Reducir movimiento', acc.reduceMotion)}
          </div>
          <div class="settings-group" style="flex-grow:1">
            <h3>Gráficos</h3>
            <div class="field-row"><label for="set-quality">Calidad</label>
              <select id="set-quality">
                <option value="high">Alta</option><option value="low">Baja (móvil)</option>
              </select></div>
          </div>
        </div>
      </div>
      <div class="set-account-bar">
        <div class="set-account-info">
          <span class="set-account-title">Cuenta</span>
          ${accountEmail ? `<span class="set-account-mail">${esc(accountEmail)}</span>` : ''}
        </div>
        <div class="set-account-actions">
          <button class="btn-ghost set-logout" id="set-logout" type="button">Cerrar sesión</button>
          <button class="btn-ghost set-reset" id="set-reset" type="button">Reiniciar PALABRIKS</button>
        </div>
      </div>`;
    const modal = this.modals.open({ title: 'AJUSTES', className: 'settings-modal', bodyHTML: body, dismissible: true });
    // Cerrar con círculo + X arriba a la derecha (como en el resto de ventanas).
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);

    // Cada cambio aplica el ajuste Y lo guarda al momento (sin depender de otros clics).
    const bind = (id, fn) => {
      const el = modal.querySelector(`#set-${id}`);
      if (el) el.addEventListener('input', () => { fn(el); this.handlers.onSettingsChanged?.(); });
    };
    // Los sliders muestran su valor en vivo (feedback).
    modal.querySelectorAll('input[type="range"]').forEach((el) => {
      const val = modal.querySelector(`#val-${el.id.replace('set-', '')}`);
      if (!val) return;
      el.addEventListener('input', () => {
        val.textContent = el.dataset.unit === 'x' ? `${parseFloat(el.value).toFixed(1)}×` : `${Math.round(parseFloat(el.value) * 100)}%`;
      });
    });
    bind('master', (el) => this.audio.setMaster(parseFloat(el.value)));
    bind('music', (el) => this.audio.setMusic(parseFloat(el.value)));
    bind('sfx', (el) => this.audio.setSfx(parseFloat(el.value)));
    bind('muted', (el) => this.audio.setMuted(el.checked));
    bind('vibration', (el) => { this.config.audio.vibration = el.checked; });
    bind('textScale', (el) => { this.config.accessibility.textScale = parseFloat(el.value); document.documentElement.style.setProperty('--text-scale', el.value); });
    bind('highContrast', (el) => { this.config.accessibility.highContrast = el.checked; document.body.classList.toggle('high-contrast', el.checked); });
    bind('reduceMotion', (el) => { this.config.accessibility.reduceMotion = el.checked; document.body.classList.toggle('reduce-motion', el.checked); });
    bind('quality', (el) => this.handlers.onQualityChange?.(el.value));
    modal.querySelector('#set-quality').value = this.config.graphics.defaultQuality === 'low' ? 'low' : 'high';

    // Cerrar sesión de Google (vuelve a la pantalla de acceso obligatorio).
    modal.querySelector('#set-logout')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.modals.close();
      this.handlers.onLogout?.();
    });

    // Reiniciar PALABRIKS: pide confirmación (acción irreversible) antes de borrar todo.
    modal.querySelector('#set-reset')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.modals.close();
      this._confirmResetAll();
    });

    modal.addEventListener('click', () => this.handlers.onSettingsChanged?.());
  }

  /** Confirmación (irreversible) para reiniciar por completo el juego. */
  _confirmResetAll() {
    this.modals.open({
      title: '⚠️ REINICIAR PALABRIKS',
      className: 'confirm-modal',
      bodyHTML: `<p class="modal-text">Se borrará <b>todo</b>: tu progreso, nivel, logros, estadísticas,
        récords y tu puesto en el ranking. <b>Esta acción no se puede deshacer.</b></p>
        <p class="modal-text sub-hint">Tu cuenta de Google seguirá conectada.</p>`,
      dismissible: true,
      buttons: [
        { label: 'Cancelar', className: 'btn-ghost', close: true },
        { label: 'Sí, reiniciar todo', className: 'btn-danger', close: true, onClick: () => this.handlers.onResetAll?.() },
      ],
    });
  }

  // --- Menús del hub (colección, diccionario, logros, etc.) ---
  openMenu(id) {
    const p = this.progression.profile;
    const builders = {
      profile: () => { this.openProfile(); return null; },
      settings: () => { this.openSettings(); return null; },
      achievements: () => { this.openAchievements(); return null; },
      collection: () => ({ title: '🎴 Colección de fichas', body: this._collectionHTML() }),
      dictionary: () => ({ title: '📖 Diccionario', body: this._dictionaryHTML(p) }),
      missions: () => ({ title: '📜 Misiones diarias', body: this._missionsHTML() }),
      ranking: () => { this.openRanking(); return null; },
      daily: () => ({ title: '📅 Palabra del día', body: this.handlers.onDailyWord?.() || '<p class="modal-text">Vuelve cada día.</p>' }),
      evolution: () => ({ title: '🧬 Evolución', body: '<p class="modal-text">Transforma palabras válidas en otras más complejas durante la partida para ganar bonificaciones. Ej: CASA → CASAS → CASITA.</p>' }),
      challenges: () => ({ title: '🎯 Desafíos', body: '<p class="modal-text">Desafíos semanales próximamente.</p>' }),
      shop: () => ({ title: '🛍️ Tienda', body: this._shopHTML() }),
      friends: () => { this.friends.openMyFriends(); return null; }, // botón del hub: directo a AMIGOS
      news: () => ({ title: `📰 Novedades de ${GAME_NAME}`, body: '<ul class="news-list"><li>MVP jugable disponible.</li><li>Evolución de palabras activada.</li><li>3 niveles de IA.</li></ul>' }),
      reward: () => { this.handlers.onDailyReward?.(); return null; },
    };
    const b = builders[id];
    if (!b) return;
    const res = b();
    if (!res) return;
    this.modals.open({ title: res.title, bodyHTML: res.body, dismissible: true, buttons: [{ label: 'Cerrar', className: 'btn-primary' }] });
  }

  _profileHTML(p) {
    const s = p.stats;
    return `<div class="stat-grid">
      ${stat('Nivel', p.level)} ${stat('Partidas', s.gamesPlayed)} ${stat('Victorias', s.wins)}
      ${stat('Palabras', s.wordsCreated)} ${stat('Evoluciones', s.evolutions)} ${stat('Familias', s.familiesCompleted)}
      ${stat('Mejor puntuación', s.bestScore)} ${stat('Palabra más larga', (s.longestWord || '—').toUpperCase())} ${stat('Racha diaria', s.dailyStreak)}
    </div>`;
  }
  _collectionHTML() {
    const rarities = ['common', 'special', 'rare', 'epic', 'legendary'];
    const names = { common: 'Común', special: 'Especial', rare: 'Rara', epic: 'Épica', legendary: 'Legendaria' };
    return `<p class="modal-text">Apariencias cosméticas (no afectan al juego).</p>
      <div class="rarity-grid">${rarities.map((r) => `<div class="rarity-card ${r}"><b>${names[r]}</b><span>Set clásico</span></div>`).join('')}</div>`;
  }
  _dictionaryHTML(p) {
    const found = p.discoveredWords.length;
    const sample = p.discoveredWords.slice(-24).map((w) => `<span class="chip">${w.toUpperCase()}</span>`).join('') || '<span class="hint">Aún no has descubierto palabras.</span>';
    return `<p class="modal-text">Has descubierto <b>${found}</b> palabras.</p><div class="chips">${sample}</div>`;
  }
  /** Icono SVG (neón) para el título de un modal, en lugar de un emoji. */
  _titleIcon(svg, color) {
    return `<span class="modal-title-ic"${color ? ` style="color:${color}"` : ''}>${svg}</span>`;
  }

  /** Cuerpo HTML del modal de logros (con estado por logro: canjeable / conseguido / progreso). */
  _achievementsBody() {
    const p = this.progression.profile;
    const claimable = this.progression.claimableCount();
    const rows = this.progression.achievements.map((a) => {
      const rec = p.achievements[a.id] || { progress: 0, unlocked: false, claimed: false };
      const prog = Math.min(rec.progress || 0, a.target);
      const pct = Math.min(100, Math.round((prog / a.target) * 100));
      const canClaim = rec.unlocked && !rec.claimed;
      const state = canClaim ? 'claimable' : rec.claimed ? 'claimed' : 'locked';
      const action = canClaim
        ? `<button class="ach2-claim" data-claim="${a.id}">Canjear</button>`
        : rec.claimed
          ? '<span class="ach2-done" title="Conseguido">✓</span>'
          : `<span class="ach2-prog">${prog}<i>/${a.target}</i></span>`;
      return `<div class="ach2 ${state}">
        <div class="ach2-ic">${ICON[a.icon] || ICON.trophy}</div>
        <div class="ach2-main">
          <div class="ach2-name">${a.label}</div>
          <div class="ach2-desc">${a.desc}</div>
          <div class="ach2-bar"><div style="width:${pct}%"></div></div>
        </div>
        <div class="ach2-side">
          <span class="ach2-xp">+${a.reward.xp} XP</span>
          ${action}
        </div>
      </div>`;
    }).join('');
    const summary = claimable > 0
      ? `<div class="ach2-summary has">
          <span class="ach2-sum-text">Tienes <b>${claimable}</b> ${claimable === 1 ? 'logro' : 'logros'} por canjear 🎁</span>
          <button class="ach2-claim-all" data-claim-all>Canjear todo</button>
        </div>`
      : '<div class="ach2-summary">No tienes logros pendientes de canjear.</div>';
    return `${summary}<div class="ach2-list">${rows}</div>`;
  }

  /** Abre el modal de LOGROS con botones de CANJEAR (otorga XP + monedas al pulsarlos). */
  openAchievements() {
    const modal = this.modals.open({ title: `${this._titleIcon(ICON.trophy, 'var(--gold)')}LOGROS`, className: 'ach-modal', bodyHTML: this._achievementsBody(), dismissible: true });
    // Botón cerrar circular arriba a la derecha (como el resto de ventanas).
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);

    const rerender = () => {
      const scale = modal.querySelector('.modal-scale');
      if (scale) scale.innerHTML = this._achievementsBody();
      wire();
      this.hub.refresh(); // actualiza el pin del menú
    };
    const wire = () => {
      // Canjear un logro concreto.
      modal.querySelectorAll('[data-claim]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const r = this.progression.claimAchievement(btn.getAttribute('data-claim'));
          if (!r) return;
          this.bus.emit('audio:sfx', { name: 'reward' });
          this.toast(`¡Canjeado! +${r.xp} XP`, { icon: '🏅', duration: 2200 });
          rerender();
        });
      });
      // Canjear TODOS de golpe.
      const allBtn = modal.querySelector('[data-claim-all]');
      if (allBtn) allBtn.addEventListener('click', () => {
        const r = this.progression.claimAllAchievements();
        if (!r.count) return;
        this.bus.emit('audio:sfx', { name: 'reward' });
        this.toast(`¡${r.count} ${r.count === 1 ? 'logro' : 'logros'} canjeados! +${r.xp} XP`, { icon: '🎁', duration: 2800 });
        rerender();
      });
    };
    wire();
  }
  _missionsHTML() {
    return `<div class="mission-list">${this.progression.missions.map((m) => `<div class="mission"><span>${m.label}</span><span class="mission-reward">+${m.reward.xp} XP · +${m.reward.coins}🪙</span></div>`).join('')}</div>`;
  }
  /** Pinta la lista del ranking. `rows` = [{name, avatar, score, self}]. */
  _rankingHTML(rows, { offline = false, guest = false } = {}) {
    // Aviso para INVITADOS: sus puntuaciones no cuentan; deben iniciar sesión con Google.
    const guestNote = guest
      ? '<p class="rank-guest-note">🔒 Juegas como <b>invitado</b>: tus puntuaciones <b>no entran</b> en el ranking. Inicia sesión con Google para competir.</p>'
      : '';
    if (!rows || !rows.length) {
      const msg = offline
        ? 'No hay conexión para cargar el ranking global.<br>Conéctate a internet e inténtalo de nuevo.'
        : 'Aún no hay partidas en el ranking.<br>¡Juega una partida para entrar!';
      return `${guestNote}<p class="modal-text">${msg}</p>`;
    }
    const pos = (i) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}º`);
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const row = (e, i) => `<div class="rank-row ${e.self ? 'me' : ''}">
      <span class="rank-pos">${pos(i)}</span>
      <span class="rank-ava">${rankAvatar(e.avatar)}</span>
      <span class="rank-name">${esc(e.name)}${e.self ? '<i>TÚ</i>' : ''}</span>
      <span class="rank-score">${e.score}</span>
    </div>`;
    const html = rows.map(row).join('');
    const selfIdx = rows.findIndex((e) => e.self);
    const globalTag = offline ? '' : '<p class="rank-foot rank-global">🌍 Ranking global</p>';
    const foot = selfIdx >= 0 ? `<p class="rank-foot">Tu mejor puesto: <b>${selfIdx + 1}º</b> de ${rows.length}</p>` : '';
    return `${guestNote}<div class="rank-list">${html}</div>${foot}${globalTag}`;
  }

  /** Abre el modal de RANKING. Lee el ranking GLOBAL de Firebase; si no hay conexión,
   *  muestra el respaldo LOCAL. */
  async openRanking() {
    const modal = this.modals.open({
      title: `${this._titleIcon(ICON.chart, '#5fe0d0')}RANKING`,
      className: 'rank-modal',
      bodyHTML: `<div class="rank-loading-box">
        <div class="spinner"></div>
        <p class="rank-loading-title">CONSTRUYENDO EL RANKING<br>DE PALABRIKS</p>
        <p class="rank-loading-sub">Conectando con el ranking global…</p>
      </div>`,
      dismissible: true,
    });
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);

    const scale = modal.querySelector('.modal-scale');
    const g = this.progression.profile.google || {};
    const guest = !!g.anon;             // invitado: no aparece en el ranking
    const uid = !guest ? (g.uid || null) : null; // solo resalta "TÚ" si estás logueado con Google
    let rows = null, offline = false;
    try {
      const remote = await this.ranking.fetchTop(100);
      rows = remote.map((r) => ({ name: r.name, avatar: r.avatar, score: r.score, self: uid && r.uid === uid }));
    } catch (e) {
      // Sin conexión / Firestore no disponible: respaldo local.
      offline = true;
      rows = this.progression.getLeaderboard();
    }
    if (scale) scale.innerHTML = this._rankingHTML(rows, { offline, guest });
  }

  // --- Partidas por turnos (asíncrono con Firebase) ---
  async openAsyncMatches(joinCode = '') {
    const modal = this.modals.open({
      title: `${this._titleIcon(ICON.users, '#7ed957')}RETA A UN JUGADOR`,
      className: 'rank-modal async-modal',
      bodyHTML: '<p class="modal-text rank-loading">Cargando tus partidas…</p>',
      dismissible: true,
    });
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);

    const scale = () => modal.querySelector('.modal-scale');
    const guest = !!this.handlers.isGuest?.();
    let joinShown = false;
    // Pide permiso de notificaciones (para el aviso "es tu turno") al abrir, en gesto de usuario.
    if (!guest) this.handlers.requestNotifyPermission?.();

    const render = async () => {
      const el = scale(); if (!el) return;
      if (guest) {
        el.innerHTML = '<p class="rank-guest-note">🔒 Las partidas por turnos necesitan una cuenta de <b>Google</b> (para guardarse en la nube). Inicia sesión para jugar.</p>';
        return;
      }
      el.innerHTML = '<p class="modal-text rank-loading">Cargando…</p>';
      const matches = (await this.handlers.onAsyncList?.()) || [];
      const myUid = this.handlers.selfUid?.();
      el.innerHTML = this._asyncHTML(matches, myUid);
      wire();
      // Enlace de invitación (?reta=CÓDIGO): abre el diálogo de unirse ya con el código puesto.
      if (joinCode && !guest && !joinShown) { joinShown = true; this._asyncJoinPrompt(joinCode); }
    };
    const wire = () => {
      const el = scale(); if (!el) return;
      el.querySelector('[data-async-new]')?.addEventListener('click', async () => {
        try {
          const r = await this.handlers.onAsyncCreate?.();
          if (r?.code) { await render(); this._asyncShareModal(r.code); }
          else render();
        } catch (e) { this.toast('No se pudo crear la partida.', { icon: '⚠️' }); render(); }
      });
      el.querySelector('[data-async-join]')?.addEventListener('click', () => this._asyncJoinPrompt());
      el.querySelectorAll('[data-async-open]').forEach((b) => b.addEventListener('click', () => {
        const m = (this._asyncCache || []).find((x) => x.id === b.getAttribute('data-async-open'));
        if (m) { this.modals.closeAll(); this.handlers.onAsyncOpen?.(m); }
      }));
      // Tocar una sala en espera → volver a abrir el panel de compartir su código.
      el.querySelectorAll('[data-async-share]').forEach((b) => b.addEventListener('click', () => {
        this._asyncShareModal(b.getAttribute('data-async-share'));
      }));
    };
    render();
  }

  _asyncHTML(matches, myUid) {
    this._asyncCache = matches;
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const top = `<div class="async-actions">
      <button class="btn btn-primary" data-async-new>➕ Nueva partida</button>
      <button class="btn btn-ghost" data-async-join>🔗 Unirse con código</button>
    </div>`;
    if (!matches.length) {
      return `${top}<p class="modal-text">No tienes partidas activas.<br>Crea una y comparte el código, o únete con el de un amigo. Puedes tener <b>varias a la vez</b>.</p>`;
    }
    const rows = matches.map((m) => {
      const opp = (m.players || []).find((p) => p.uid !== myUid);
      const oppName = esc(opp?.name || 'Esperando rival…');
      let label = '', cls = '', attr = '', tag = 'div';
      if (m.status === 'waiting') {
        label = `Esperando rival · código <b>${esc(m.code)}</b> · 📤 Compartir`;
        attr = `data-async-share="${esc(m.code)}"`; tag = 'button';
      } else if (m.status === 'finished') {
        label = m.winnerUid ? (m.winnerUid === myUid ? '🏆 Ganaste' : 'Perdiste') : 'Terminada';
        cls = 'done';
      } else if (m.turnUid === myUid) {
        label = '▶ ¡TU TURNO!'; cls = 'me'; attr = `data-async-open="${esc(m.id)}"`; tag = 'button';
      } else {
        label = '⏳ Esperando a tu rival';
      }
      return `<${tag} class="async-row ${cls}" ${attr}>
        <span class="ar-opp">${oppName}</span>
        <span class="ar-state">${label}</span>
      </${tag}>`;
    }).join('');
    return `${top}<div class="async-list">${rows}</div>`;
  }

  /** Acorta una URL con TinyURL (permite CORS). Devuelve la corta o null si falla. */
  async _shortenUrl(url) {
    try {
      const r = await fetch('https://tinyurl.com/api-create.php?url=' + encodeURIComponent(url));
      if (!r.ok) return null;
      const short = (await r.text()).trim();
      return /^https?:\/\//.test(short) ? short : null;
    } catch (_) { return null; }
  }

  /** Panel para COMPARTIR el código de invitación por WhatsApp / Facebook / Instagram / nativo. */
  _asyncShareModal(code) {
    if (!code) return;
    const cc = String(code).toUpperCase();
    // URL de invitación. Se ACORTA con TinyURL en segundo plano (para no ver la URL larga).
    let url = `${location.origin}${location.pathname}?reta=${cc}`;
    const text = () => `¡Te reto en PALABRIKS! 🧩 Únete con el código ${cc}\n${url}`;
    this._shortenUrl(url).then((s) => { if (s) url = s; });
    const modal = this.modals.open({
      title: 'INVITA A TU RIVAL',
      className: 'lobby-modal async-share-modal',
      dismissible: true,
      bodyHTML: `<p class="sub-hint">Comparte este código para que tu amigo se una a la partida.</p>
        <div class="share-code">${cc}</div>
        <div class="share-grid">
          <button class="share-btn share-wa" data-share="wa">🟢 WhatsApp</button>
          <button class="share-btn share-fb" data-share="fb">🔵 Facebook</button>
          <button class="share-btn share-ig" data-share="ig">📸 Instagram</button>
          <button class="share-btn share-native" data-share="native">📤 Compartir…</button>
          <button class="share-btn share-copy" data-share="copy">📋 Copiar código</button>
        </div>`,
    });
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);

    const openUrl = (u) => { try { window.open(u, '_blank', 'noopener'); } catch (_) {} };
    const nativeShare = async () => {
      if (navigator.share) { try { await navigator.share({ title: 'PALABRIKS', text: text(), url }); return true; } catch (_) { return false; } }
      return false;
    };
    const copyCode = async () => {
      try { await navigator.clipboard.writeText(cc); this.toast('Código copiado.', { icon: '📋' }); }
      catch (_) { this.toast(`Tu código: ${cc}`, { icon: '📋', duration: 3000 }); }
    };
    modal.querySelectorAll('[data-share]').forEach((b) => b.addEventListener('click', async () => {
      const k = b.getAttribute('data-share');
      if (k === 'wa') openUrl(`https://wa.me/?text=${encodeURIComponent(text())}`);
      else if (k === 'fb') openUrl(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
      else if (k === 'ig') { if (!(await nativeShare())) { await copyCode(); this.toast('Copiado. Ábrelo en Instagram y pégalo.', { icon: '📸', duration: 3400 }); } }
      else if (k === 'native') { if (!(await nativeShare())) await copyCode(); }
      else if (k === 'copy') await copyCode();
    }));
  }

  _asyncJoinPrompt(prefill = '') {
    const modal = this.modals.open({
      title: 'UNIRSE POR CÓDIGO',
      className: 'lobby-modal',
      dismissible: true,
      bodyHTML: `<p class="sub-hint">Introduce el código que te pasó tu amigo.</p>
        <input id="async-code" class="code-input" maxlength="5" placeholder="CÓDIGO" autocomplete="off" value="${String(prefill || '').toUpperCase().replace(/[^A-Z0-9]/g, '')}" style="text-transform:uppercase" />`,
    });
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.modals.close());
    modal.appendChild(closeBtn);
    const go = document.createElement('button');
    go.className = 'lobby-start';
    go.innerHTML = '▶';
    go.addEventListener('click', async () => {
      const code = modal.querySelector('#async-code')?.value?.trim()?.toUpperCase();
      if (!code) return;
      go.disabled = true;
      const r = await this.handlers.onAsyncJoin?.(code);
      if (r?.error) { this.toast(r.error, { icon: '⚠️' }); go.disabled = false; return; }
      this.modals.closeAll(); // entra directo a jugar tu turno
    });
    modal.appendChild(go);
  }

  _shopHTML() {
    const items = [
      { icon: '🎨', name: 'Set de fichas “Bosque”', price: '250🪙' },
      { icon: '🪵', name: 'Mesa de roble', price: '400🪙' },
      { icon: '✨', name: 'Efecto de evolución dorado', price: '3💎' },
    ];
    return `<div class="shop-list">${items.map((i) => `<div class="shop-item"><span class="si-ic">${i.icon}</span><span>${i.name}</span><button class="btn btn-sm" disabled>${i.price}</button></div>`).join('')}</div>
      <p class="hint">Compras cosméticas (sin ventaja competitiva). Próximamente.</p>`;
  }

  startTutorial(onFinish) {
    this.tutorial = new TutorialManager(this.root, { bus: this.bus, game: this.game, onFinish });
    this.tutorial.start();
  }
}

// --- helpers de plantilla ---
function slider(id, label, value, min = 0, max = 1) {
  const pct = max <= 1 ? `${Math.round(value * 100)}%` : `${(+value).toFixed(1)}×`;
  return `<div class="field-row"><label for="set-${id}">${label}</label>
    <div class="slider-ctl">
      <input id="set-${id}" type="range" min="${min}" max="${max}" step="0.05" value="${value}"
        data-unit="${max <= 1 ? 'pct' : 'x'}">
      <span class="slider-val" id="val-${id}">${pct}</span>
    </div></div>`;
}
// Interruptor tipo "switch" (feedback claro ON/OFF, cómodo en móvil).
function toggle(id, label, checked) {
  return `<div class="field-row"><label for="set-${id}">${label}</label>
    <label class="switch"><input id="set-${id}" type="checkbox" ${checked ? 'checked' : ''}><span class="switch-track"></span></label></div>`;
}
function stat(label, value) {
  return `<div class="stat-cell"><span class="stat-val">${value}</span><span class="stat-label">${label}</span></div>`;
}
